import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DeviceDetail } from '@mantelpiece/shared';
import { api, type ListedPhoto, type StoredSession } from '../lib/api';
import type { Guard } from '../pages/Device';
import { FramePicker } from './FramePicker';
import { PhotoTextForm } from './PhotoText';

interface Props {
  readonly session: StoredSession;
  readonly device: DeviceDetail;
  readonly guard: Guard;
  readonly status: 'approved' | 'pending';
  /** Refreshes the badge on the Waiting tab once the queue moves. */
  readonly onReviewed?: (() => void) | undefined;
}

/** A run of photos from the same album, kept together in the approval queue. */
interface Group {
  readonly key: string;
  readonly label: string | null;
  readonly photos: readonly ListedPhoto[];
}

/**
 * The queue is grouped by where the photos came from.
 *
 * A guest publishing a two-hundred-photo album to a frame with approval switched
 * on must not become two hundred separate decisions, and the natural unit of
 * that decision is the album — so it is the unit the list is built from.
 */
const groupBySource = (photos: readonly ListedPhoto[]): readonly Group[] => {
  const order: string[] = [];
  const buckets = new Map<string, ListedPhoto[]>();
  const labels = new Map<string, string | null>();

  for (const photo of photos) {
    const key = photo.source?.albumId ?? 'loose';
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      labels.set(key, photo.source?.albumLabel ?? null);
      order.push(key);
    }
    bucket.push(photo);
  }

  return order.map((key) => ({
    key,
    label: labels.get(key) ?? null,
    photos: buckets.get(key) ?? [],
  }));
};

/**
 * The contact sheet, in both of its modes. The approved list is what is on the
 * TV; the pending list is the approval queue. They differ only in which buttons
 * sit on a print, which is not enough to justify two components that then drift.
 */
export const PhotosTab = ({ session, device, guard, status, onReviewed }: Props) => {
  const [photos, setPhotos] = useState<readonly ListedPhoto[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [sendTo, setSendTo] = useState<readonly string[]>([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  /** Which print has its details open. One at a time — the form is tall. */
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      await guard(async () => {
        const result = await api.listPhotos(session.token, device.id, status, before);
        setPhotos((current) => (before ? [...current, ...result.photos] : result.photos));
        setCursor(result.nextCursor);
      });
      setLoading(false);
    },
    [device.id, guard, session.token, status],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const drop = (ids: readonly string[]) =>
    setPhotos((current) => current.filter((item) => !ids.includes(item.id)));

  const act = (ids: readonly string[], run: () => Promise<void>) =>
    guard(async () => {
      await run();
      drop(ids);
      setPicked((current) => current.filter((id) => !ids.includes(id)));
      onReviewed?.();
    });

  const groups = useMemo(() => groupBySource(photos), [photos]);

  const toggle = (id: string) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );

  const pickedAssets = photos
    .filter((photo) => picked.includes(photo.id))
    .map((photo) => photo.assetId);

  const sendOn = async () => {
    setSending(true);
    setSent(null);
    await guard(async () => {
      const result = await api.placeAssets(session.token, pickedAssets, sendTo);
      const totals = Object.values(result.placed).reduce(
        (sum, tally) => ({
          sent: sum.sent + tally.sent,
          queued: sum.queued + tally.queued,
          already: sum.already + tally.already,
        }),
        { sent: 0, queued: 0, already: 0 },
      );
      const said: string[] = [];
      if (totals.sent) said.push(`${totals.sent} sent`);
      if (totals.queued) said.push(`${totals.queued} waiting for approval`);
      if (totals.already) said.push(`${totals.already} already there`);
      setSent(said.join(' · ') || 'Nothing to send.');
      setPicked([]);
      setSendTo([]);
    });
    setSending(false);
  };

  if (!loading && photos.length === 0) {
    return (
      <p className="empty">
        {status === 'pending'
          ? 'Nothing waiting. Photos only land here when the person who sent them is not on the auto-accept list.'
          : device.permissions.canSend
            ? 'Nothing on the frame yet. Add some from the Add photos tab, or invite somebody from the People tab.'
            : 'Nothing on this frame yet.'}
      </p>
    );
  }

  return (
    <section className="stack-loose">
      {status === 'pending' ? (
        groups.map((group) => (
          <div key={group.key}>
            <div className="section-head">
              <p className="eyebrow">
                {group.label
                  ? `From the album “${group.label}” · ${group.photos.length}`
                  : `Sent one by one · ${group.photos.length}`}
              </p>
              {/* One decision for the whole group is the point of grouping it. */}
              <span className="row-tight">
                <button
                  type="button"
                  className="button button-small"
                  onClick={() => {
                    const ids = group.photos.map((photo) => photo.id);
                    void act(ids, async () => {
                      await api.approvePhotos(session.token, device.id, ids);
                    });
                  }}
                >
                  Put all {group.photos.length} up
                </button>
                <button
                  type="button"
                  className="button button-quiet button-small"
                  onClick={() => {
                    const ids = group.photos.map((photo) => photo.id);
                    void act(ids, async () => {
                      await api.rejectPhotos(session.token, device.id, ids);
                    });
                  }}
                >
                  No thanks
                </button>
              </span>
            </div>

            <ul className="sheet">
              {group.photos.map((photo, index) => (
                <li key={photo.id} className="print" data-state="static">
                  <div className="print-image">
                    <img
                      src={photo.thumbUrl}
                      alt={photo.caption ?? `Photo from ${photo.uploader}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <p className="print-caption">
                    <span className="print-frame">{String(index + 1).padStart(3, '0')}</span>
                    <span className="print-status">{photo.uploader}</span>
                  </p>
                  <div className="print-actions">
                    <button
                      type="button"
                      className="button button-small"
                      onClick={() =>
                        void act([photo.id], async () => {
                          await api.approvePhotos(session.token, device.id, [photo.id]);
                        })
                      }
                    >
                      Put it up
                    </button>
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      onClick={() =>
                        void act([photo.id], async () => {
                          await api.rejectPhotos(session.token, device.id, [photo.id]);
                        })
                      }
                    >
                      No
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      ) : (
        <div>
          <div className="section-head">
            <p className="eyebrow">
              {picked.length > 0
                ? `${picked.length} chosen`
                : `On the frame · ${photos.length} in rotation`}
            </p>
            {picked.length > 0 && (
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() => {
                  setPicked([]);
                  setSendTo([]);
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Sending these on moves no bytes at all — the pictures already
              exist, so another frame is another row. */}
          {picked.length > 0 && (
            <div className="stack" style={{ marginBottom: '1.5rem' }}>
              <p className="eyebrow">Send these to</p>
              <FramePicker
                session={session}
                selected={sendTo}
                onChange={setSendTo}
                exclude={[device.id]}
                emptyText="No other frames you can send to."
              />
              {sendTo.length > 0 && (
                <p>
                  <button
                    type="button"
                    className="button button-small"
                    disabled={sending}
                    onClick={() => void sendOn()}
                  >
                    {sending
                      ? 'Sending'
                      : `Send ${picked.length} to ${sendTo.length} frame${
                          sendTo.length === 1 ? '' : 's'
                        }`}
                  </button>
                </p>
              )}
            </div>
          )}

          {sent && (
            <p className="alert" data-tone="ok">
              {sent}
            </p>
          )}

          <ul className="sheet">
            {photos.map((photo, index) => {
              const on = picked.includes(photo.id);
              return (
                <li key={photo.id} className="print" data-state="static" data-picked={on}>
                  <button
                    type="button"
                    className="print-pick"
                    aria-pressed={on}
                    onClick={() => toggle(photo.id)}
                  >
                    <span className="print-image">
                      <img
                        src={photo.thumbUrl}
                        alt={photo.caption ?? `Photo from ${photo.uploader}`}
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                    <span className="visually-hidden">
                      {on ? 'Chosen' : `Choose the photo from ${photo.uploader}`}
                    </span>
                  </button>
                  <p className="print-caption">
                    <span className="print-frame">{String(index + 1).padStart(3, '0')}</span>
                    <span className="print-status">
                      {photo.source ? photo.source.albumLabel : photo.uploader}
                    </span>
                  </p>

                  {(photo.caption || photo.location) && (
                    <p className="note">
                      {photo.caption}
                      {photo.caption && photo.location ? ' · ' : ''}
                      {photo.location}
                    </p>
                  )}

                  {/* Only offered where the server would accept it. An
                      album-owned photo says where its text lives instead —
                      reconcile would overwrite anything typed here. */}
                  {photo.canEditText ? (
                    editing === photo.id ? (
                      <PhotoTextForm
                        value={{ caption: photo.caption, location: photo.location }}
                        reach="This changes the photo on this frame only."
                        onCancel={() => setEditing(null)}
                        onSave={async (text) => {
                          await api.editPhoto(session.token, device.id, photo.id, text);
                          setPhotos((current) =>
                            current.map((entry) =>
                              entry.id === photo.id ? { ...entry, ...text } : entry,
                            ),
                          );
                          setEditing(null);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="button button-quiet button-small"
                        onClick={() => setEditing(photo.id)}
                      >
                        {photo.caption || photo.location ? 'Edit details' : 'Add details'}
                      </button>
                    )
                  ) : (
                    photo.source && (
                      <p className="note">
                        Its caption comes from the album “{photo.source.albumLabel}”.
                      </p>
                    )
                  )}

                  {/* Guests get the button too — the server lets them remove
                      their own, and hiding it from everybody would mean asking
                      the owner to clean up your mistake. */}
                  <button
                    type="button"
                    className="print-remove"
                    onClick={() =>
                      void act([photo.id], () =>
                        api.deletePhoto(session.token, device.id, photo.id),
                      )
                    }
                    aria-label={
                      device.permissions.canManage
                        ? `Remove photo ${index + 1} from the frame`
                        : `Remove the photo you sent, number ${index + 1}`
                    }
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {cursor !== null && (
        <p>
          <button
            type="button"
            className="button button-quiet"
            disabled={loading}
            onClick={() => void load(cursor)}
          >
            {loading ? 'Loading' : 'Load older'}
          </button>
        </p>
      )}
    </section>
  );
};
